import { HttpClient } from '@angular/common/http';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { User } from '@angular/fire/auth';
import { Router } from '@angular/router';
import { environment } from '@environments/environment';
import { AuthService } from '@suvo-bi-core';
import { CurrentTenantService, ITenant, ITenantRecipe, TenantRecipeService } from '@suvo-bi-users';
import { Subject, lastValueFrom } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
  standalone: false,
  selector: 'app-new-shop',
  templateUrl: './new-shop.component.html',
  styleUrl: './new-shop.component.scss',
})
export class NewShopComponent implements OnInit, OnDestroy {
  constructor(
    private readonly authService: AuthService,
    private readonly angularFireAuth: AngularFireAuth,
    private readonly currentTenantService: CurrentTenantService,
    private readonly http: HttpClient,
    private readonly router: Router,
    private readonly tenantRecipeService: TenantRecipeService,
  ) {}

  private readonly unsubscribe$ = new Subject<void>();

  readonly questionnaireFormGroup = new UntypedFormGroup({});

  readonly formGroup = new UntypedFormGroup({
    questionnaire: this.questionnaireFormGroup,
  });

  isAuthResolved = false;
  isExistingUser = false;
  isSubmitting = false;
  isLoggingOut = false;
  signedInUserLabel = '';
  formError = '';

  ngOnInit() {
    this.angularFireAuth.user.pipe(takeUntil(this.unsubscribe$)).subscribe((user) => {
      this.isExistingUser = !!user;
      this.signedInUserLabel = this.getUserLabel(user);
      if (this.isExistingUser) {
        this.applyQuestionnaireDefaults();
        this.questionnaireFormGroup.updateValueAndValidity({ emitEvent: false });
      }
      this.isAuthResolved = true;
    });
  }

  ngOnDestroy() {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  private applyQuestionnaireDefaults() {
    if (!this.questionnaireFormGroup.contains('priorExperience')) {
      this.questionnaireFormGroup.addControl(
        'priorExperience',
        new UntypedFormGroup({
          hasUsedECommerce: new UntypedFormControl(false),
          hasUsedBI: new UntypedFormControl(false),
          none: new UntypedFormControl(true),
        }),
      );
    }

    if (!this.questionnaireFormGroup.contains('purposes')) {
      this.questionnaireFormGroup.addControl(
        'purposes',
        new UntypedFormGroup({
          monetiseData: new UntypedFormControl(false),
          analyseData: new UntypedFormControl(false),
          makeAPI: new UntypedFormControl(false),
          reportsOrPresentation: new UntypedFormControl(false),
          unsure: new UntypedFormControl(true),
        }),
      );
    }
  }

  async onSubmit() {
    if (this.isSubmitting) {
      return;
    }

    this.isSubmitting = true;
    this.formError = '';
    this.ensureQuestionnaireDefaults();
    if (this.formGroup.controls.shopDetails?.invalid) {
      this.formError = 'Please complete the shop details before continuing.';
      this.isSubmitting = false;
      return;
    }
    const recipe: ITenantRecipe = this.formGroup.value;

    try {
      recipe.email = JSON.parse(localStorage.getItem('onboarding')).email;
    } catch (e) {}

    try {
      let tenantRecipeID: string | undefined;
      ({ _id: tenantRecipeID } = await this.tenantRecipeService.create(recipe));

      const currentUser = this.authService.currentUser();

      if (currentUser) {
        let tenant: ITenant | undefined;
        const token = await this.angularFireAuth.currentUser?.then((user) => user?.getIdToken());
        tenant = await lastValueFrom(
          this.http.post<ITenant>(
            `${environment.dataServiceApi}tenant/existing`,
            {
              tenantRecipeID,
              email: currentUser.email,
            },
            token ? { headers: { Authorization: `Bearer ${token}` } } : undefined,
          ),
        );

        if (token) {
          await lastValueFrom(
            this.http.post(
              `${environment.usersServiceApi}auth/refresh-claims`,
              {},
              { headers: { Authorization: `Bearer ${token}` } },
            ),
          );
        }

        // Force a token refresh so new tenant roles are available immediately after creation.
        await this.angularFireAuth.currentUser?.then((user) => user?.getIdToken(true));

        await this.currentTenantService.init(
          `${environment.usersServiceApi}tenant/${tenant.firebaseTenantId}`,
        );

        await this.router.navigate(['/', 'shops']);
      } else {
        // Prompt an unregistered user to register
        await this.router.navigate(['/', 'register'], { queryParams: { recipe: tenantRecipeID } });
      }
    } catch (error) {
      this.handleSubmitError(error);
    } finally {
      this.isSubmitting = false;
    }
  }

  private handleSubmitError(error: unknown) {
    const message =
      (error as { error?: { message?: string } })?.error?.message ??
      (error as { message?: string })?.message ??
      '';

    if (
      message.includes('duplicate key error') ||
      message.includes('subdomain_1') ||
      message.includes('subdomain is already taken')
    ) {
      this.formError =
        'That shop subdomain is already in use. Please choose a different subdomain.';
      return;
    }

    this.formError = 'We could not create the shop. Please try again.';
  }

  private ensureQuestionnaireDefaults() {
    this.applyQuestionnaireDefaults();
    this.questionnaireFormGroup.updateValueAndValidity({ emitEvent: false });
  }

  async onLogout() {
    if (this.isLoggingOut) {
      return;
    }

    this.isLoggingOut = true;
    try {
      await this.angularFireAuth.signOut();
    } finally {
      this.isLoggingOut = false;
    }
  }

  private getUserLabel(user: User | null): string {
    if (!user) {
      return '';
    }

    return user.displayName || user.email || 'Logged in user';
  }
}
